import type { SupportedRepubName } from './types';

export type ProductCardConfig = {
  descriptionKey: 'trendsDescription' | 'evDescription' | 'premiumTipstersDescription' | null;
  descriptionText?: string;
  image: {
    alt: string;
    filename: string;
    position: 'leading' | 'trailing';
  };
  listItems: string[];
  testId: string;
  title: string;
  trackingPosition: 1 | 2 | 3 | 4;
};

export type LandingPageConfig = {
  defaultPageForSubscribedUser: (repubPath: string) => string;
  evDescription: string;
  heroSubtitle: string;
  heroTitle: string;
  paywallRetiredLandsOnMatchCentre?: boolean;
  plansTitle: string;
  premiumTipstersDescription?: string;
  productCards: ProductCardConfig[];
  trendsDescription: string;
};

export const LANDING_PAGE_CONFIGS = {
  GZ: {
    evDescription: `La nostra IA elabora oltre 100 milioni di previsioni ogni settimana e analizza 125 milioni di variazioni di quota al giorno. Con lo strumento EV (Expected Value) ti mostriamo le giocate dove i dati indicano un vantaggio rispetto al valore delle quote. Con la nostra IA scegli il livello di rischio e scopri le quote più promettenti.`,
    heroSubtitle: `La migliore IA per il betting sul mercato si unisce all'esperienza secolare de La Gazzetta dello Sport per offrirti uno strumento in grado di trovare le quote con il più alto Valore Atteso nel mercato`,
    heroTitle: `Usa i dati e l'IA per passare in vantaggio`,
    plansTitle: 'Scegli un Piano',
    premiumTipstersDescription: `Niente guru, niente colpi di genio. I nostri modelli di intelligenza artificiale analizzano ogni giorno 125 milioni di dati, migliorano le scelte partita dopo partita e generano nuove dritte basate solo su numeri e statistiche.`,
    trendsDescription: `Attraverso un'IA avanzata e milioni di dati esclusivi, hai subito a portata di mano le statistiche per fare la differenza. Scopri in pochi clic i trend di performance delle ultime 5, 10 o 20 partite, i mercati più popolari e i giocatori da tenere d'occhio. Sfrutta Gazzetta Ai Predictor per decidere la tua prossima mossa e restare in vantaggio.`,
    defaultPageForSubscribedUser: (repubPath: string) => `${repubPath}/plus/value-bets`,
    productCards: [
      {
        descriptionKey: 'premiumTipstersDescription',
        image: {
          alt: 'Image of Premium Tipsters card',
          filename: 'premium-tipsters.png',
          position: 'leading',
        },
        listItems: [
          'Pronostici quotidiani generati da sistemi avanzati',
          'Profili Tipster AI con strategie e rischi differenti',
          'Performance storiche e bilancio sempre a tua disposizione',
        ],
        testId: 'product-card-premium-tipsters',
        title: 'Premium Tipster',
        trackingPosition: 2,
      },
      {
        descriptionKey: 'evDescription',
        image: {
          alt: 'Image of Positive Value Bets card',
          filename: 'positive-value-bets.png',
          position: 'leading',
        },
        listItems: [
          '+100 pronostici e quote con EV positivo al giorno',
          'Calcolo della Probabilità AI per ogni scommessa',
          'Tutti gli operatori comparati in tempo reale',
          'Studi e approfondimenti per ogni scommessa',
        ],
        testId: 'product-card-ev',
        title: 'Scommesse di Valore',
        trackingPosition: 1,
      },
      {
        descriptionKey: 'trendsDescription',
        image: {
          alt: 'Image of Trends card',
          filename: 'trends.png',
          position: 'leading',
        },
        listItems: ['Andamento performance', 'Mercati del momento', 'Giocatori rivelazione'],
        testId: 'product-card-trends',
        title: 'Trend',
        trackingPosition: 2,
      },
      {
        descriptionKey: null,
        descriptionText: `Scopri i mercati su cui si sta giocando di più e come si stanno muovendo gli altri scomettitori; accedi ai dati in tempo reale grazie a Sportradar. Ogni giorno monitoriamo oltre 125 milioni di variazioni di quote, per offrirti le più convenienti in ogni momento. Resta in vantaggio con Gazzetta Ai Predictor!`,
        image: {
          alt: 'Image of Public Betting Splits card',
          filename: 'public-betting-splits.png',
          position: 'leading',
        },
        listItems: ['Flussi di giocata', 'Flussi di denaro', 'Comparazione quote'],
        testId: 'product-card-pbd',
        title: 'Su cosa si gioca?',
        trackingPosition: 2,
      },
    ] as ProductCardConfig[],
  },
  WS: {
    evDescription:
      "Combining WhoScored's rich dataset with over 100 million projections each week AND 125 million price changes each day, uncover which bets the bookies have under-estimated and where there is value in the market. Processing millions of data points instantly, our AI tool allows you to see where you may have an edge and what the best value bets will be across all European top leagues (and more).",
    heroSubtitle:
      'With over 41 million data points every week on WhoScored now powered by provably accurate AI projections, WhoScored+ helps you win more, more often with your soccer betting.',
    heroTitle: 'More Intelligent, More Profitable, More Often.',
    plansTitle: 'Step Up Your Game - Subscribe Now!',
    trendsDescription:
      "Supercharge your knowledge of any key player or team performance - intertwined with future-projections - to help uncover the best betting opportunities in 10+ leagues worldwide on any valid matchday. Take away the hassle of deciphering multiple key stats and performance patterns and insights with Trends' simple visualisation over the past 5, 10 or 20 games, helping accelerate your betting decisions and improving your likelihood of profitable betting.",
    defaultPageForSubscribedUser: (repubPath: string) => `${repubPath}/plus/value-bets`,
    productCards: [
      {
        descriptionKey: 'trendsDescription',
        image: {
          alt: 'Image of Trends card',
          filename: 'trends.png',
          position: 'leading',
        },
        listItems: ['Performance Trends', 'Hottest Plays', 'Breakout Players'],
        testId: 'product-card-trends',
        title: 'Trends',
        trackingPosition: 2,
      },
      {
        descriptionKey: 'evDescription',
        image: {
          alt: 'Image of Positive Value Bets card',
          filename: 'positive-value-bets.png',
          position: 'trailing',
        },
        listItems: ['Best Odds Available', 'Current Trends for Each Pick', 'AI and Implied Probability'],
        testId: 'product-card-ev',
        title: 'Positive Value Bets',
        trackingPosition: 1,
      },
    ] as ProductCardConfig[],
  },
  US: {
    evDescription:
      "Our AI produces 100 million projections each week. We scour over 125 million price changes each day. Our Positive EV tool gives you our best plays where you have a mathematical edge over the books. We've thrown in recent trends to show you whether a pick is hot so you've got everything you need to beat the odds!",
    heroSubtitle: '25+ years of experience beating the books combined with cutting-edge AI geared to help you win.',
    heroTitle: 'Bet Smarter With Industry-leading data and ai',
    plansTitle: 'Plans',
    trendsDescription:
      'Our AI models, intertwined with market-leading data, enable you access to in-depth statistical analysis in an instant and digestible form. Identify the best betting opportunities with easy navigation and intuitive visualization of the key stats and performance trends over the past 5, 10 or 20 games, as well as pulling out the hottest props and breakout players. Gain a breakdown of recent games across all key stats that could factor into your bet.',
    defaultPageForSubscribedUser: (repubPath: string) => `${repubPath}/plus/positive-ev`,
    paywallRetiredLandsOnMatchCentre: true,
    productCards: [
      {
        descriptionKey: 'trendsDescription',
        image: {
          alt: 'Image of Trends card',
          filename: 'trends.png',
          position: 'leading',
        },
        listItems: ['Performance Trends', 'Hottest Plays', 'Breakout Players'],
        testId: 'product-card-trends',
        title: 'Trends',
        trackingPosition: 2,
      },
      {
        descriptionKey: 'evDescription',
        image: {
          alt: 'Image of Positive Value Bets card',
          filename: 'positive-value-bets.png',
          position: 'trailing',
        },
        listItems: ['Best Odds Available', 'Current Trends for Each Pick', 'AI and Implied Probability'],
        testId: 'product-card-ev',
        title: 'Positive Value Bets',
        trackingPosition: 1,
      },
      {
        descriptionKey: null,
        descriptionText:
          'Spot the difference between the public and big money sharps! Get the most up-to-date betting splits powered by Sportradar, the official data provider for the MLB, NBA, and NHL. We monitor 125 million price changes each day, so you always catch the best odds. Stay one step ahead with oddschecker+!',
        image: {
          alt: 'Image of Public Betting Splits card',
          filename: 'public-betting-splits.png',
          position: 'leading',
        },
        listItems: ['Public Bet %', 'Public Money %', 'Best Odds Across All Sportsbooks'],
        testId: 'product-card-pbd',
        title: 'Public Betting Splits',
        trackingPosition: 2,
      },
    ] as ProductCardConfig[],
  },
  OC: {
    evDescription:
      "Our AI produces 100 million projections each week. We scour over 125 million price changes each day. Our Positive EV tool gives you our best plays where you have a mathematical edge over the books. We've thrown in recent trends to show you whether a pick is hot so you've got everything you need to beat the odds!",
    heroSubtitle: '25+ years of experience beating the books combined with cutting-edge AI geared to help you win.',
    heroTitle: 'Bet Smarter With Industry-leading data and ai',
    plansTitle: 'Plans',
    trendsDescription:
      'Our AI models, intertwined with market-leading data, enable you access to in-depth statistical analysis in an instant and digestible form. Identify the best betting opportunities with easy navigation and intuitive visualization of the key stats and performance trends over the past 5, 10 or 20 games, as well as pulling out the hottest props and breakout players. Gain a breakdown of recent games across all key stats that could factor into your bet.',
    defaultPageForSubscribedUser: (repubPath: string) => `${repubPath}/plus/value-bets`,
    paywallRetiredLandsOnMatchCentre: true,
    productCards: [
      {
        descriptionKey: 'trendsDescription',
        image: {
          alt: 'Image of Trends card',
          filename: 'trends.png',
          position: 'leading',
        },
        listItems: ['Performance Trends', 'Hottest Plays', 'Breakout Players'],
        testId: 'product-card-trends',
        title: 'Trends',
        trackingPosition: 2,
      },
      {
        descriptionKey: 'evDescription',
        image: {
          alt: 'Image of Positive Value Bets card',
          filename: 'positive-value-bets.png',
          position: 'trailing',
        },
        listItems: ['Best Odds Available', 'Current Trends for Each Pick', 'AI and Implied Probability'],
        testId: 'product-card-ev',
        title: 'Positive Value Bets',
        trackingPosition: 1,
      },
      {
        descriptionKey: null,
        descriptionText:
          'Spot the difference between the public and big money sharps! Get the most up-to-date betting splits powered by Sportradar, the official data provider for the MLB, NBA, and NHL. We monitor 125 million price changes each day, so you always catch the best odds. Stay one step ahead with oddschecker+!',
        image: {
          alt: 'Image of Public Betting Splits card',
          filename: 'public-betting-splits.png',
          position: 'leading',
        },
        listItems: ['Public Bet %', 'Public Money %', 'Best Odds Across All Sportsbooks'],
        testId: 'product-card-pbd',
        title: 'Public Betting Splits',
        trackingPosition: 2,
      },
    ] as ProductCardConfig[],
  },
} satisfies Record<SupportedRepubName, LandingPageConfig>;
